import type { AuditEvent } from '@stage/contracts';
import { newRequestId, parseError } from './client.js';

/**
 * Analytics & operations API client (M7, §26, §34.3). Serves the per-gig and cross-gig analytics
 * dashboards, the workspace audit-event log, and the workspace export. Shares the
 * {@link ApiError}/`ErrorEnvelope` contract and bearer-token pattern of {@link createAdminClient}.
 *
 * §26.2 privacy: aggregate metrics that have no data are transmitted as `null` (missing), NEVER as
 * `0`. This client preserves `null` verbatim so the UI can distinguish "no data" from a real zero.
 *
 * The client never stores tokens: it reads the current access token through the injected getter on
 * each request so silent refresh (§32.3) is transparent to callers.
 */

const BASE = '/v1';

/** One QR/source-code bucket in the per-gig source breakdown (§26.3). */
export interface SourceBreakdownEntry {
  sourceCode: string;
  count: number;
}

/**
 * Per-gig aggregate (§26.1). `counts` values may be `null` where a metric has no data (§26.2) —
 * render `null` as "—", never as `0`. Money totals are integer minor units keyed by currency.
 */
export interface GigAnalytics {
  gigId: string;
  counts: Record<string, number | null>;
  sourceBreakdown: SourceBreakdownEntry[] | null;
  grossTipByCurrencyMinor: Record<string, number> | null;
  conversionRateBySession: number | null;
  peakInteractionHour: string | null;
}

/** A ranked dimension bucket (tempo category, key, meter, groove family) (§26.1). */
export interface RankedValue {
  value: string;
  count: number;
}

/** A most-requested-song row with its performed count (§26.1). */
export interface RequestedSong {
  songId: string;
  requests: number;
  performed: number;
}

/** Cross-gig (per-artist) aggregate (§26.1). Any field is `null` when there is no data yet. */
export interface ArtistAnalytics {
  mostRequestedSongs: RequestedSong[] | null;
  requestToPerformedRate: number | null;
  mostSelectedTempoCategories: RankedValue[] | null;
  mostSelectedKeys: RankedValue[] | null;
  mostSelectedMeters: RankedValue[] | null;
  mostSelectedGrooveFamilies: RankedValue[] | null;
}

/** A page of audit events with an opaque forward cursor (§27.24, §34). */
export interface AuditEventsPage {
  items: AuditEvent[];
  nextCursor: string | null;
}

/**
 * Workspace export document (§34.3). A JSON snapshot of configuration, repertoire, gigs,
 * interactions, aggregate analytics, tips, and exportable audit history. Secrets are excluded.
 */
export interface ExportResult {
  generatedAt: string;
  workspaceId: string;
  counts: Record<string, number>;
  [key: string]: unknown;
}

export interface AnalyticsClient {
  getGigAnalytics(gigId: string): Promise<GigAnalytics>;
  getArtistAnalytics(artistId: string): Promise<ArtistAnalytics>;
  listAuditEvents(workspaceId: string, cursor?: string): Promise<AuditEventsPage>;
  createExport(workspaceId: string): Promise<ExportResult>;
}

/**
 * Create an analytics client bound to a live access-token getter, mirroring
 * {@link createAdminClient}. The getter is read per request so silent refresh is transparent (§32.3).
 */
export function createAnalyticsClient(getAccessToken: () => string | null): AnalyticsClient {
  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const token = getAccessToken();
    const headers = new Headers(init?.headers);
    headers.set('accept', 'application/json');
    headers.set('x-request-id', newRequestId());
    if (token) headers.set('authorization', `Bearer ${token}`);
    if (init?.body && !headers.has('content-type')) {
      headers.set('content-type', 'application/json');
    }
    const res = await fetch(`${BASE}${path}`, { ...init, headers });
    if (!res.ok) throw await parseError(res);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  const enc = encodeURIComponent;

  return {
    getGigAnalytics(gigId) {
      return request<GigAnalytics>(`/gigs/${enc(gigId)}/analytics`);
    },
    getArtistAnalytics(artistId) {
      return request<ArtistAnalytics>(`/artists/${enc(artistId)}/analytics`);
    },
    listAuditEvents(workspaceId, cursor) {
      const qs = cursor ? `?cursor=${enc(cursor)}` : '';
      return request<AuditEventsPage>(`/workspaces/${enc(workspaceId)}/audit-events${qs}`);
    },
    createExport(workspaceId) {
      return request<ExportResult>(`/workspaces/${enc(workspaceId)}/exports`, { method: 'POST' });
    },
  };
}
